import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, Terminal, Sparkles, MessageCircle, LayoutDashboard, LogOut } from 'lucide-react';
import { BOT_CONFIG } from '../config/bot';
import BotAvatar from './BotAvatar';
import { useAuth } from '../contexts/AuthContext';

const navLinks = [
  { name: 'Home', path: '/', icon: Home },
  { name: 'Commands', path: '/commands', icon: Terminal },
  { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { name: 'Contact', path: '/contact', icon: MessageCircle },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, login, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <>
      <nav className="fixed top-0 inset-x-0 z-50 flex justify-center pointer-events-none">
        <div
          className={`
            pointer-events-auto mt-4 mx-4 w-full max-w-4xl
            flex items-center justify-between
            px-3 sm:px-5 py-2.5
            rounded-2xl transition-all duration-500
            ${scrolled
              ? 'glass-heavy shadow-lg shadow-black/30'
              : 'bg-white/[0.02] border border-transparent'
            }
          `}
        >
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2.5 group shrink-0">
            <div className="relative w-9 h-9 rounded-full overflow-hidden ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all duration-300">
              <BotAvatar className="w-full h-full object-cover" size={64} />
            </div>
            <span className="text-sm font-display font-bold text-gradient hidden sm:block">
              {BOT_CONFIG.name}
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const active = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`
                    relative px-4 py-2 text-[13px] font-medium rounded-xl
                    transition-all duration-300
                    ${active
                      ? 'text-white bg-white/[0.08]'
                      : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                    }
                  `}
                >
                  {link.name}
                  {active && (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-[2px] rounded-full bg-primary" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <a
              href={BOT_CONFIG.inviteLink}
              target="_blank"
              rel="noopener noreferrer"
              className="
                hidden sm:flex items-center gap-2
                px-5 py-2 rounded-xl
                bg-gradient-primary text-white text-[13px] font-semibold
                hover:shadow-lg hover:shadow-primary/25 hover:-translate-y-0.5
                active:scale-[0.97]
                transition-all duration-300
              "
            >
              <Sparkles className="w-3.5 h-3.5" />
              Add to Discord
            </a>

            {/* Auth Button */}
            {isAuthenticated && user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-1 rounded-xl hover:bg-white/[0.06] transition-colors"
                >
                  <img
                    src={user.avatar}
                    alt={user.displayName}
                    className="w-8 h-8 rounded-full ring-2 ring-primary/20"
                  />
                </button>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 w-48 bg-dark-800 border border-white/[0.08] rounded-xl shadow-xl z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/[0.06]">
                        <p className="text-white text-sm font-medium truncate">{user.displayName}</p>
                        <p className="text-gray-500 text-xs truncate">@{user.username}</p>
                      </div>
                      <Link
                        to="/dashboard"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-gray-300 hover:text-white hover:bg-white/[0.04] text-sm transition-colors"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                      </Link>
                      <button
                        onClick={() => { logout(); setUserMenuOpen(false); }}
                        className="flex items-center gap-2 w-full px-4 py-2.5 text-red-400 hover:text-red-300 hover:bg-white/[0.04] text-sm transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={login}
                className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl bg-[#5865F2]/10 hover:bg-[#5865F2]/20 border border-[#5865F2]/30 text-[#8b9cf7] text-[13px] font-medium transition-all"
              >
                Login
              </button>
            )}

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`
          fixed inset-0 z-40 md:hidden
          transition-all duration-300
          ${mobileOpen ? 'visible' : 'invisible pointer-events-none'}
        `}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${mobileOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setMobileOpen(false)}
        />

        {/* Panel */}
        <div
          className={`
            absolute top-20 left-4 right-4 max-w-md mx-auto
            glass-heavy rounded-2xl p-4 space-y-1.5
            transition-all duration-300
            ${mobileOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}
          `}
        >
          {navLinks.map((link) => {
            const Icon = link.icon;
            const active = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium
                  transition-all duration-200
                  ${active
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-300 hover:bg-white/[0.04] hover:text-white'
                  }
                `}
              >
                <Icon className="w-4.5 h-4.5" />
                {link.name}
              </Link>
            );
          })}

          <div className="h-px bg-white/[0.06] my-2" />

          <a
            href={BOT_CONFIG.inviteLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-5 py-3.5 bg-gradient-primary text-white font-semibold rounded-xl text-sm active:scale-[0.97] transition-transform"
          >
            <Sparkles className="w-4 h-4" />
            Add to Discord
          </a>

          {/* Mobile Login/Logout */}
          {isAuthenticated && user ? (
            <div className="flex items-center gap-3 px-4 py-3 mt-1">
              <img src={user.avatar} alt="" className="w-8 h-8 rounded-full" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user.displayName}</p>
              </div>
              <button
                onClick={() => { logout(); setMobileOpen(false); }}
                className="p-2 text-red-400 hover:text-red-300"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => { login(); setMobileOpen(false); }}
              className="flex items-center justify-center gap-2 px-5 py-3.5 bg-[#5865F2]/10 border border-[#5865F2]/30 text-[#8b9cf7] font-semibold rounded-xl text-sm active:scale-[0.97] transition-transform mt-1"
            >
              Login with Discord
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;
